import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { startOfMonth } from 'date-fns'
import { Calendar, Clock, CreditCard, Loader2, Lock, MapPin, Plus } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useCancelEvent,
  useEvents,
  useEventsInRange,
  usePublishEvent,
} from '~/entities/event/model/use-events'
import { useActiveProfileId } from '~/entities/profile/model/use-profiles'
import { useEventGate } from '~/entities/subscription/model/use-event-gate'
import { planName } from '~/entities/subscription/plans'
import { RenderedEventCard } from '~/features/events/components'
import { CancelEventDescription } from '~/features/events/components/cancel-event-description'
import { DeleteEventDialog } from '~/features/events/components/delete-event-dialog'
import { EventCalendar } from '~/features/events/components/event-calendar'
import { EventsToolbar, type EventsView } from '~/features/events/components/events-toolbar'
import { monthGridRange, monthParam, parseMonthParam } from '~/features/events/lib/event-calendar'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { copyToClipboard, getEventShareUrl } from '~/shared/lib/share'
import { useInfiniteScroll } from '~/shared/lib/use-infinite-scroll'
import { DESTRUCTIVE_OUTLINE_BUTTON } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { CardGridSkeleton } from '~/shared/ui/card-grid-skeleton'
import { PageHeader } from '~/widgets/page-header'

interface EventsSearch {
  view?: EventsView
  month?: string
  q?: string
}

export const Route = createFileRoute('/dashboard/events/')({
  validateSearch: (search: Record<string, unknown>): EventsSearch => ({
    view: search.view === 'calendar' ? 'calendar' : undefined,
    month: parseMonthParam(search.month) ? (search.month as string) : undefined,
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
  }),
  component: EventsList,
})

type ConfirmationDialog = {
  type: 'publish' | 'cancel'
  eventId: string
  eventTitle: string
  hasCoverImage?: boolean
}

function EventsList() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const view = search.view ?? 'list'
  const query = search.q ?? ''
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id || ''

  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming')

  // Two queries - one per tab. The inactive tab still fetches page 1 so
  // its count badge has a real number. Walking subsequent pages on scroll
  // only happens for the active tab (its sentinel is the only one mounted).
  const hostProfileId = useActiveProfileId(businessId)
  const allUpcoming = useEvents(businessId, 'upcoming', hostProfileId)
  const allPast = useEvents(businessId, 'past', hostProfileId)
  const upcoming = useEvents(businessId, 'upcoming', hostProfileId, query)
  const past = useEvents(businessId, 'past', hostProfileId, query)
  const active = activeTab === 'upcoming' ? upcoming : past

  const publishEvent = usePublishEvent(businessId)
  const cancelEvent = useCancelEvent(businessId)

  const { tier, limits, usage } = usePlan()

  const sentinelRef = useInfiniteScroll({
    hasNextPage: active.hasNextPage,
    isFetchingNextPage: active.isFetchingNextPage,
    fetchNextPage: active.fetchNextPage,
  })

  const now = useMemo(() => new Date(), [])
  const isLoading =
    allUpcoming.isLoading || allPast.isLoading || upcoming.isLoading || past.isLoading
  const totalEvents = allUpcoming.total + allPast.total

  const month = useMemo(
    () => parseMonthParam(search.month) ?? startOfMonth(now),
    [search.month, now],
  )
  const monthRange = useMemo(() => monthGridRange(month), [month])
  const calendar = useEventsInRange(businessId, monthRange, {
    hostProfileId,
    search: query,
    enabled: view === 'calendar',
  })

  const updateSearch = useCallback(
    (patch: EventsSearch) =>
      navigate({ search: (prev: EventsSearch) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  )
  const handleSearchChange = useCallback(
    (q: string) => updateSearch({ q: q.trim() ? q : undefined }),
    [updateSearch],
  )
  const handleViewChange = useCallback(
    (next: EventsView) => updateSearch({ view: next === 'calendar' ? 'calendar' : undefined }),
    [updateSearch],
  )
  const handleMonthChange = useCallback(
    (next: Date) =>
      updateSearch({
        month: monthParam(next) === monthParam(startOfMonth(now)) ? undefined : monthParam(next),
      }),
    [updateSearch, now],
  )

  const {
    atLimit: activeEventsAtLimit,
    canCreate: canCreateEvent,
    isUnlimited: isUnlimitedActive,
    used: activeEventsUsed,
    max: activeEventsMax,
  } = useEventGate()
  const isUnlimitedDrafts = limits.maxDraftEvents === -1
  const draftsAtLimit = !isUnlimitedDrafts && usage.draftEvents >= limits.maxDraftEvents
  const handleDelete = useCallback((eventId: string, eventTitle: string) => {
    setDeleteTarget({ id: eventId, title: eventTitle })
  }, [])

  const handlePublish = useCallback(
    (eventId: string, eventTitle: string, hasCoverImage: boolean) => {
      if (!hasCoverImage) {
        toast.error(m.cannotPublishEvent(), {
          description: m.pleaseAddACoverImageBeforePublishing(),
        })
        return
      }
      setConfirmDialog({ type: 'publish', eventId, eventTitle, hasCoverImage })
    },
    [],
  )

  const handleCancel = useCallback((eventId: string, eventTitle: string) => {
    setConfirmDialog({ type: 'cancel', eventId, eventTitle })
  }, [])

  const handleShare = useCallback(async (eventId: string) => {
    const ok = await copyToClipboard(getEventShareUrl(eventId))
    if (ok) {
      toast.success(m.linkCopied(), { description: m.linkCopiedToClipboard() })
    } else {
      toast.error(m.failedToCopyLink())
    }
  }, [])

  const handleCopy = useCallback(
    (id: string) => {
      navigate({ to: '/dashboard/events/create', search: { copyFrom: id } })
    },
    [navigate],
  )

  const handleConfirmAction = async () => {
    if (!confirmDialog) return

    try {
      switch (confirmDialog.type) {
        case 'publish':
          await publishEvent.mutateAsync(confirmDialog.eventId)
          toast.success(m.eventPublished(), {
            description: m.eventIsNowLiveAndVisibleToUsers({
              eventTitle: confirmDialog.eventTitle,
            }),
          })
          break
        case 'cancel':
          await cancelEvent.mutateAsync(confirmDialog.eventId)
          toast.success(m.eventCanceled(), {
            description: m.theEventHasBeenMarkedAsCanceled(),
          })
          break
      }
      setConfirmDialog(null)
    } catch (error) {
      const errorMessage = getErrorMessageFromError(error)

      switch (confirmDialog.type) {
        case 'publish':
          toast.error(m.failedToPublishEvent(), { description: errorMessage })
          break
        case 'cancel':
          toast.error(m.failedToCancelEvent(), { description: errorMessage })
          break
      }
    }
  }

  if (isLoading) {
    return <CardGridSkeleton keyPrefix="event-card-skeleton" />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.events()}
        description={m.manageYourBusinessEventsAndActivities()}
        action={
          totalEvents > 1 ? (
            activeEventsAtLimit ? (
              <div className="flex flex-wrap gap-2">
                <Button disabled>
                  <Lock className="mr-2 h-4 w-4" />
                  {m.createEvent()}
                </Button>
                <Link to="/dashboard/subscription">
                  <Button variant="outline">
                    <CreditCard className="mr-2 h-4 w-4" />
                    {m.upgradeNow()}
                  </Button>
                </Link>
              </div>
            ) : (
              <Link to="/dashboard/events/create">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {m.createEvent()}
                </Button>
              </Link>
            )
          ) : undefined
        }
      />

      {activeEventsAtLimit && (
        <PlanLimitBanner
          message={m.activeEventsLimitReached({
            count: String(limits.maxActiveEvents),
            plan: planName(tier),
          })}
        />
      )}
      {draftsAtLimit && (
        <PlanLimitBanner
          message={m.draftsLimitReached({
            count: String(limits.maxDraftEvents),
            plan: planName(tier),
          })}
        />
      )}

      {!isUnlimitedActive && (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <Badge variant="outline" className="gap-1.5 font-normal">
            {m.activeEventsUsage({
              used: String(activeEventsUsed),
              max: String(activeEventsMax),
            })}
          </Badge>
          {!isUnlimitedDrafts && (
            <Badge variant="outline" className="gap-1.5 font-normal">
              {m.draftsUsage({
                used: String(usage.draftEvents),
                max: String(limits.maxDraftEvents),
              })}
            </Badge>
          )}
        </div>
      )}

      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.type === 'publish' && m.publishEvent()}
              {confirmDialog?.type === 'cancel' && m.cancelEvent()}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.type === 'publish' && m.thisWillMakeTheEventVisibleToAllUsers()}
              {confirmDialog?.type === 'cancel' && (
                <CancelEventDescription businessId={businessId} eventId={confirmDialog.eventId} />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmDialog?.type === 'cancel' ? DESTRUCTIVE_OUTLINE_BUTTON : ''}
            >
              {confirmDialog?.type === 'publish' && m.publish()}
              {confirmDialog?.type === 'cancel' && m.cancelEvent()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DeleteEventDialog
        businessId={businessId}
        eventId={deleteTarget?.id ?? null}
        eventTitle={deleteTarget?.title}
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />

      {/* Events Grid */}
      {totalEvents === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">{m.noEventsYet()}</h2>
            <p className="text-muted-foreground text-center mb-4">
              {m.createYourFirstEventToStartEngagingWithCustomers()}
            </p>
            <CreateEventCta atLimit={activeEventsAtLimit} atLimitClassName="space-y-2" />
          </CardContent>
        </Card>
      ) : (
        <>
          <EventsToolbar
            search={query}
            onSearchChange={handleSearchChange}
            view={view}
            onViewChange={handleViewChange}
          />

          {view === 'calendar' ? (
            <EventCalendar
              month={month}
              onMonthChange={handleMonthChange}
              events={calendar.data ?? []}
              isFetching={calendar.isFetching}
              now={now}
            />
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={activeTab === 'upcoming' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('upcoming')}
                >
                  {m.upcomingEvents()}
                  <span className="ml-2 rounded-full bg-current/15 px-1.5 py-0.5 text-xs font-medium leading-none opacity-80">
                    {upcoming.total}
                  </span>
                </Button>
                <Button
                  variant={activeTab === 'past' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveTab('past')}
                >
                  {m.pastEvents()}
                  <span className="ml-2 rounded-full bg-current/15 px-1.5 py-0.5 text-xs font-medium leading-none opacity-80">
                    {past.total}
                  </span>
                </Button>
              </div>

              <div>
                {activeTab === 'upcoming' && (
                  <div>
                    {upcoming.total === 0 ? (
                      <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                          <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
                          <h2 className="text-xl font-semibold mb-2">
                            {query ? m.eventsNoSearchResults({ query }) : m.noUpcomingEvents()}
                          </h2>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="auto-grid">
                        {upcoming.events.map((event) => (
                          <RenderedEventCard
                            key={event.id}
                            event={event}
                            variant="upcoming"
                            now={now}
                            isPendingDelete={!!deleteTarget}
                            isPendingPublish={publishEvent.isPending}
                            isPendingCancel={cancelEvent.isPending}
                            onDelete={handleDelete}
                            onPublish={handlePublish}
                            onCancel={handleCancel}
                            onCopy={handleCopy}
                            onShare={handleShare}
                          />
                        ))}

                        {/* Ghost card - shown when only one event exists */}
                        {totalEvents === 1 && !query && (
                          <Card
                            className={`overflow-hidden flex flex-col border-dashed bg-muted/30 relative group transition-colors ${
                              canCreateEvent ? 'cursor-pointer hover:bg-muted/50' : ''
                            }`}
                          >
                            {canCreateEvent && (
                              <Link
                                to="/dashboard/events/create"
                                className="absolute inset-0 z-10"
                              />
                            )}
                            <div className="aspect-video w-full overflow-hidden bg-muted/50 flex items-center justify-center">
                              <Calendar className="h-12 w-12 text-muted-foreground/40" />
                            </div>
                            <CardHeader>
                              <CardTitle className="text-muted-foreground/60">
                                {m.yourNextEvent()}
                              </CardTitle>
                              <CardDescription className="text-muted-foreground/50">
                                {m.createAnotherEventToEngageYourAudience()}
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="flex-1 flex flex-col">
                              <div className="space-y-3 flex-1 text-muted-foreground/40">
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4" />
                                  <div className="h-4 w-32 bg-muted-foreground/10 rounded" />
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <MapPin className="h-4 w-4" />
                                  <div className="h-4 w-24 bg-muted-foreground/10 rounded" />
                                </div>
                                <div className="h-4 w-16 bg-muted-foreground/10 rounded" />
                              </div>
                              <div className="flex gap-2 pt-4 mt-auto">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  disabled={!canCreateEvent}
                                >
                                  {canCreateEvent ? (
                                    <Plus className="mr-2 h-4 w-4" />
                                  ) : (
                                    <Lock className="mr-2 h-4 w-4" />
                                  )}
                                  {m.createEvent()}
                                </Button>
                              </div>
                              {!canCreateEvent && (
                                <Link to="/dashboard/subscription" className="mt-2">
                                  <Button size="sm" className="w-full">
                                    <CreditCard className="mr-2 h-4 w-4" />
                                    {m.upgradeNow()}
                                  </Button>
                                </Link>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'past' && (
                  <div>
                    {past.total === 0 ? (
                      <Card>
                        <CardContent className="flex flex-col items-center justify-center py-12">
                          <Calendar className="h-16 w-16 text-muted-foreground mb-4" />
                          <h2 className="text-xl font-semibold mb-2">
                            {query ? m.eventsNoSearchResults({ query }) : m.noPastEvents()}
                          </h2>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="auto-grid">
                        {past.events.map((event) => (
                          <RenderedEventCard
                            key={event.id}
                            event={event}
                            variant="past"
                            now={now}
                            isPendingDelete={!!deleteTarget}
                            isPendingPublish={publishEvent.isPending}
                            isPendingCancel={cancelEvent.isPending}
                            onDelete={handleDelete}
                            onPublish={handlePublish}
                            onCancel={handleCancel}
                            onCopy={handleCopy}
                            onShare={handleShare}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Sentinel - triggers fetchNextPage on the active tab when scrolled
						    into view. Only mounted when there's actually another page. */}
                {active.hasNextPage && <div ref={sentinelRef} aria-hidden className="h-1" />}

                {active.isFetchingNextPage && (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

function PlanLimitBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
      <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
      <p className="flex-1 text-sm text-amber-700 dark:text-amber-300">{message}</p>
      <Link to="/dashboard/subscription">
        <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs shrink-0">
          <CreditCard className="w-3 h-3" />
          {m.upgradePlan()}
        </Button>
      </Link>
    </div>
  )
}

function CreateEventCta({
  atLimit,
  atLimitClassName,
}: {
  atLimit: boolean
  atLimitClassName: string
}) {
  if (atLimit) {
    return (
      <div className={atLimitClassName}>
        <Button disabled>
          <Lock className="mr-2 h-4 w-4" />
          {m.createEvent()}
        </Button>
        <Link to="/dashboard/subscription">
          <Button variant="outline">
            <CreditCard className="mr-2 h-4 w-4" />
            {m.upgradeNow()}
          </Button>
        </Link>
      </div>
    )
  }
  return (
    <Link to="/dashboard/events/create">
      <Button>
        <Plus className="mr-2 h-4 w-4" />
        {m.createEvent()}
      </Button>
    </Link>
  )
}
